import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import type { Ad } from "@models/Ad.model";
import type { AdCardMenuAction } from "./types";

const TYPE_LABEL: Record<string, string> = {
    video: "Video",
    image: "Image",
    sms: "SMS",
    email: "Email",
    document: "Document",
    form: "Form",
    container: "Container",
};

/*
    What the card calls the ad's type (MPN-1530, Rachel's report).

    Campaign ads are containers, so every card read "Container" where she expects Video
    or Image. A container declares what it holds in `subtype`, and when that is unset —
    which happens on real ads, "Snag Your Spot" in prod among them — the children answer
    for it.

    Same precedence as the Monitor Ads tab resolves server-side, so the two surfaces
    cannot disagree about one ad: declared subtype, then the commonest child, then the
    bare type.
*/
export function typeLabel(ad: Ad): string {
    const resolved = ad.type === "container" ? containerType(ad) : ad.type;
    return TYPE_LABEL[resolved] ?? resolved;
}

function containerType(ad: Ad): string {
    // "container" is not an answer. Campaign ads nest: the one in the campaign is a
    // container whose subtype is literally "container", holding a format group
    // (subtype "video") holding the videos. Taking subtype at face value badged every
    // tile "Container" even after a fix that passed its tests.
    if (ad.subtype && ad.subtype !== "container") return ad.subtype;

    const counts = new Map<string, number>();
    // Any depth, not just direct children — the only child is often another container.
    const walk = (node: Ad) => {
        for (const child of node.children ?? []) {
            if (!child?.type) continue;
            if (child.type === "container") {
                if (child.subtype && child.subtype !== "container") {
                    counts.set(child.subtype, (counts.get(child.subtype) ?? 0) + 1);
                }
                walk(child);
                continue;
            }
            counts.set(child.type, (counts.get(child.type) ?? 0) + 1);
        }
    };
    walk(ad);

    if (counts.size === 0) return ad.type;

    // Commonest wins, ties broken by name so the label cannot flicker between renders.
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
}

type Props = {
    ad: Ad;
    menuActions: AdCardMenuAction[];
    hideType?: boolean;
    hideMenu?: boolean;
    /** Name of the template this ad was rebranded from, when the caller is an internal
        surface that knows it. Deliberately a prop and not a field on Ad: the same Ad payload
        is served to the client review panel, which must never show it (MPN-1508). */
    sourceTemplateName?: string | null;
};

export function AdCardHeader({ ad, menuActions, hideType, hideMenu, sourceTemplateName }: Props) {
    return (
        <div className="adcard__head">
            {/* Name and provenance stack rather than sitting side by side. Sharing one row
                left the template label a few characters of a narrow card, so it rendered as
                "from 10..." and told nobody anything. Below the title it gets the card's full
                width, and the title attribute still covers the rare name long enough to clip. */}
            <span className="adcard__titles">
                <span className="adcard__name">{ad.name || "Untitled Ad"}</span>
                {/* An ad gets renamed to describe the rebrand, which is what the client should
                    read, but internally you still need to know which template it came from. Shown
                    only when the name has actually diverged: repeating an identical name would be
                    noise on every un-retitled ad. */}
                {sourceTemplateName && sourceTemplateName !== ad.name && (
                    <span
                        className="adcard__template"
                        title={`Rebranded from "${sourceTemplateName}"`}
                    >
                        from {sourceTemplateName}
                    </span>
                )}
            </span>
            {!hideType && <span className="adcard__type">{typeLabel(ad)}</span>}
            {!hideMenu && (
            <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                    <button
                        type="button"
                        className="adcard__menu"
                        aria-label="Ad actions"
                        onClick={(e) => e.stopPropagation()}
                    >
                        ⋯
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        className="radix-context-menu-content"
                        align="end"
                        sideOffset={4}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {menuActions.map((action) => (
                            <DropdownMenu.Item
                                key={action.label}
                                className={[
                                    "radix-context-menu-item",
                                    action.danger ? "radix-context-menu-item--danger" : "",
                                ].join(" ").trim()}
                                onSelect={action.onSelect}
                                disabled={action.disabled}
                            >
                                {action.label}
                            </DropdownMenu.Item>
                        ))}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
            )}
        </div>
    );
}
